import { callAIEndpoint } from './aiClient';

const ENDPOINT = '/api/ai/chat-completion';
const STREAM_TIMEOUT_MS = 30_000; // 30 s hard timeout

export async function getChatCompletion(
  provider: string,
  model: string,
  messages: object[],
  parameters: object = {}
) {
  return callAIEndpoint(ENDPOINT, {
    provider,
    model,
    messages,
    stream: false,
    parameters,
  });
}

export async function getStreamingChatCompletion(
  provider: string,
  model: string,
  messages: object[],
  onChunk: (chunk: any) => void,
  onComplete: () => void,
  onError: (error: Error) => void,
  parameters: object = {},
  signal?: AbortSignal
) {
  // Timeout controller — cancel if no data arrives within STREAM_TIMEOUT_MS
  const timeoutId = setTimeout(() => {
    onError(new Error('Response timed out. Please try again.'));
  }, STREAM_TIMEOUT_MS);

  try {
    const response = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ provider, model, messages, stream: true, parameters }),
      signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || `HTTP error: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('Response body is not readable');

    const decoder = new TextDecoder();
    let buffer = '';
    let receivedDone = false;

    try {
      while (true) {
        // Respect external abort
        if (signal?.aborted) {
          reader.cancel();
          break;
        }

        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        // Keep the last (possibly incomplete) line in the buffer
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data: ')) continue;
          const raw = trimmed.slice(6).trim();
          if (!raw || raw === '[DONE]') continue;

          try {
            const data = JSON.parse(raw);
            if (data.type === 'chunk' && data.chunk) {
              onChunk(data.chunk);
            } else if (data.type === 'done') {
              receivedDone = true;
            } else if (data.type === 'error') {
              console.error('API Route Error:', { error: data.error, details: data.details });
              throw new Error(data.error || 'Stream error from server');
            }
          } catch (parseErr) {
            // Skip malformed JSON lines — don't crash the stream
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    // Flush any remaining buffer content
    if (buffer.trim().startsWith('data: ')) {
      try {
        const raw = buffer.trim().slice(6).trim();
        if (raw && raw !== '[DONE]') {
          const data = JSON.parse(raw);
          if (data.type === 'chunk' && data.chunk) onChunk(data.chunk);
        }
      } catch {
        // ignore
      }
    }

    if (!signal?.aborted) {
      onComplete();
    }
  } catch (error) {
    clearTimeout(timeoutId);
    if (signal?.aborted) {
      // Aborted intentionally — not an error
      return;
    }
    console.error('Streaming error:', error);
    onError(error instanceof Error ? error : new Error('Streaming error'));
  }
}
